/**
 * Module Subscription Card
 * Card component for managing individual module subscription
 */

import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { protectedResources } from '../../config/msalConfig';
import {
    Card,
    Switch,
    Button,
    Select,
    Space,
    Typography,
    Tag,
    Tooltip,
    message,
    Modal,
    Form,
    InputNumber
} from 'antd';
import {
    CheckCircleOutlined,
    SettingOutlined,
    SendOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import {
    subscribeToModule,
    updateSubscription,
    unsubscribeFromModule,
    triggerManualDigest
} from '../../services/digestService';

const { Text, Title } = Typography;
const { Option } = Select;

export default function ModuleSubscriptionCard({ module, preferences, onSubscriptionChange }) {
    const { instance, accounts } = useMsal();
    const [loading, setLoading] = useState(false);
    const [settingsVisible, setSettingsVisible] = useState(false);
    const [form] = Form.useForm();

    const isSubscribed = module.IsSubscribed === 1;

    const handleToggleSubscription = async (checked) => {
        try {
            setLoading(true);

            const response = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });

            if (checked) {
                // Subscribe with default settings
                await subscribeToModule(
                    module.ModuleId,
                    {
                        frequency: preferences?.DefaultFrequency || 'weekly',
                        deliveryHour: preferences?.DefaultDeliveryHour || 8,
                        detailLevel: 'summary'
                    },
                    response.accessToken
                );
                message.success(`Subscribed to ${module.ModuleName}`);
            } else {
                // Unsubscribe
                await unsubscribeFromModule(module.ModuleId, response.accessToken);
                message.success(`Unsubscribed from ${module.ModuleName}`);
            }

            onSubscriptionChange();
        } catch (error) {
            console.error('Error toggling subscription:', error);
            message.error(`Failed to ${checked ? 'subscribe to' : 'unsubscribe from'} module`);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateSettings = async (values) => {
        try {
            setLoading(true);

            const response = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });

            await updateSubscription(
                module.SubscriptionId,
                {
                    frequency: values.frequency,
                    deliveryHour: values.deliveryHour,
                    detailLevel: values.detailLevel,
                    isActive: true
                },
                response.accessToken
            );

            message.success('Subscription settings updated');
            setSettingsVisible(false);
            onSubscriptionChange();
        } catch (error) {
            console.error('Error updating settings:', error);
            message.error('Failed to update settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSendTestDigest = async () => {
        try {
            setLoading(true);

            const response = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });

            await triggerManualDigest(module.ModuleId, null, response.accessToken);
            message.success('Test digest sent! Check your email.');
        } catch (error) {
            console.error('Error sending test digest:', error);
            message.error('Failed to send test digest');
        } finally {
            setLoading(false);
        }
    };

    const getFrequencyLabel = (frequency) => {
        const labels = {
            daily: 'Daily',
            weekly: 'Weekly',
            monthly: 'Monthly'
        };
        return labels[frequency] || frequency;
    };

    return (
        <>
            <Card
                hoverable
                style={{
                    height: '100%',
                    borderColor: isSubscribed ? '#52c41a' : '#d9d9d9'
                }}
                bodyStyle={{ padding: '20px' }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                }}>
                    <div style={{ flex: 1 }}>
                        <Title level={5} style={{ margin: 0, marginBottom: '4px' }}>
                            {module.ModuleName}
                        </Title>
                        {isSubscribed && (
                            <Tag
                                icon={<CheckCircleOutlined />}
                                color="success"
                                style={{ marginBottom: '8px' }}
                            >
                                Subscribed
                            </Tag>
                        )}
                    </div>
                    <Switch
                        checked={isSubscribed}
                        onChange={handleToggleSubscription}
                        loading={loading}
                    />
                </div>

                {/* Description */}
                {module.Description && (
                    <Text type="secondary" style={{
                        display: 'block',
                        marginBottom: '16px',
                        fontSize: '13px'
                    }}>
                        {module.Description}
                    </Text>
                )}

                {/* Subscription details */}
                {isSubscribed && (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px',
                            background: '#f5f5f5',
                            borderRadius: '4px'
                        }}>
                            <ClockCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                            <Text style={{ fontSize: '13px' }}>
                                Frequency: <strong>{getFrequencyLabel(preferences?.DefaultFrequency)}</strong>
                            </Text>
                        </div>

                        {/* Actions */}
                        <Space style={{ marginTop: '8px', width: '100%' }}>
                            <Tooltip title="Configure subscription settings">
                                <Button
                                    type="default"
                                    size="small"
                                    icon={<SettingOutlined />}
                                    onClick={() => setSettingsVisible(true)}
                                >
                                    Settings
                                </Button>
                            </Tooltip>
                            <Tooltip title="Send a test digest email now">
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<SendOutlined />}
                                    onClick={handleSendTestDigest}
                                    loading={loading}
                                >
                                    Test
                                </Button>
                            </Tooltip>
                        </Space>
                    </Space>
                )}

                {/* Call to action for unsubscribed */}
                {!isSubscribed && (
                    <div style={{
                        textAlign: 'center',
                        padding: '16px 0',
                        color: '#8c8c8c'
                    }}>
                        <Text type="secondary">
                            Enable to receive automated digest emails for this module
                        </Text>
                    </div>
                )}
            </Card>

            {/* Settings Modal */}
            <Modal
                title={`Subscription Settings - ${module.ModuleName}`}
                open={settingsVisible}
                onCancel={() => setSettingsVisible(false)}
                onOk={() => form.submit()}
                confirmLoading={loading}
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        frequency: preferences?.DefaultFrequency || 'weekly',
                        deliveryHour: preferences?.DefaultDeliveryHour || 8,
                        detailLevel: 'summary'
                    }}
                    onFinish={handleUpdateSettings}
                >
                    <Form.Item
                        label="Frequency"
                        name="frequency"
                        rules={[{ required: true, message: 'Please select frequency' }]}
                    >
                        <Select>
                            <Option value="daily">Daily - Every day at delivery hour</Option>
                            <Option value="weekly">Weekly - Every Monday at delivery hour</Option>
                            <Option value="monthly">Monthly - 1st of month at delivery hour</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Delivery Hour (UTC)"
                        name="deliveryHour"
                        rules={[
                            { required: true, message: 'Please select delivery hour' },
                            { type: 'number', min: 0, max: 23, message: 'Hour must be 0-23' }
                        ]}
                        extra="Time in UTC when you want to receive digest emails"
                    >
                        <InputNumber
                            min={0}
                            max={23}
                            style={{ width: '100%' }}
                            formatter={value => `${value}:00`}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Detail Level"
                        name="detailLevel"
                        rules={[{ required: true, message: 'Please select detail level' }]}
                    >
                        <Select>
                            <Option value="summary">Summary - Brief overview with key insights</Option>
                            <Option value="detailed">Detailed - Comprehensive analysis with all data points</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}